import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, EMPTY, interval, Observable, of, throwError } from "rxjs";
import { catchError, finalize, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { AuthenticationService } from "../authentication/authentication.service";

export interface LicenseStatus {
  valid: boolean;
  error?: string;
  daysRemaining: number;
  payload?: any;
}

@Injectable({ providedIn: "root" })
export class LicenseService {
  private http = inject(HttpClient);
  private authenticationService = inject(AuthenticationService);

  private status$ = new BehaviorSubject<LicenseStatus | null>(null);
  private inFlight$: Observable<LicenseStatus | null> | null = null;
  url: string;

  constructor() {
    this.url = this.authenticationService.getWebserviceURL("license");

    this.refresh();

    interval(1000 * 60 * 60)
      // status needs a signed-in user; a 401 after sign-out must not end the hourly poll.
      .pipe(switchMap(() => this.http.get<LicenseStatus>(this.url + "status", { headers: this.authenticationService.headers }).pipe(catchError(() => EMPTY))))
      .subscribe((s) => this.status$.next(s));
  }

  getStatus$() {
    return this.status$.asObservable();
  }

  refresh() {
    this.url = this.authenticationService.getWebserviceURL("license");

    if (this.url.length < 15 || !this.authenticationService.hasAuthToken()) {
      return;
    }

    this.fetchStatus().subscribe();
  }

  ensureStatus$(): Observable<LicenseStatus | null> {
    if (this.status$.value) {
      return of(this.status$.value);
    }

    this.url = this.authenticationService.getWebserviceURL("license");

    if (this.url.length < 15) {
      return of(null);
    }

    return this.inFlight$ ?? this.fetchStatus();
  }

  private fetchStatus(): Observable<LicenseStatus | null> {
    const request$ = this.http.get<LicenseStatus>(this.url + "status", { headers: this.authenticationService.headers }).pipe(
      tap((s) => this.status$.next(s)),
      catchError(() => of(null)),
      finalize(() => {
        if (this.inFlight$ === request$) {
          this.inFlight$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.inFlight$ = request$;
    return request$;
  }

  get(): Observable<any> {
    this.url = this.authenticationService.getWebserviceURL("license");

    if (this.url.length < 15) {
      return;
    }

    return this.http.get<LicenseStatus>(this.url + "status", { headers: this.authenticationService.headers }).pipe(
      map((data: any) => {
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getHwid(): Observable<string> {
    return this.http.get<string>(this.url + "hwid", { headers: this.authenticationService.headers }).pipe(
      map((data: any) => {
        return data.hwid;
      }),
      catchError(this.handleError),
    );
  }

  upload(file: File) {
    const formData = new FormData();
    formData.append("File", file);

    return this.http.post<LicenseStatus>(this.url + "upload", formData, {
      headers: this.authenticationService.uploadHeaders,
    });
  }

  private handleError = (error: Response) => {
    return throwError(() => error);
  };
}
